import { Message } from '../types';
import { SYSTEM_PROMPT } from '../systemPrompt';

export const sendMessage = async (messages: Message[]): Promise<string> => {
  const apiStartTime = Date.now();
  console.log('⏱️ API call started at:', new Date().toLocaleTimeString());
  
  try {
    const response = await fetch('/api/openai/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: [
          {
            role: 'system',
            content: SYSTEM_PROMPT
          },
          ...messages.map(msg => ({
            role: msg.role as 'user' | 'assistant',
            content: msg.content
          }))
        ]
      })
    });

    if (!response.ok) {
      const errorData = await response.text();
      console.error('API error:', errorData);
      throw new Error('Failed to send message');
    }

    const data = await response.json();
    
    const apiEndTime = Date.now();
    const apiResponseTime = apiEndTime - apiStartTime;
    console.log(`⏱️ API Response Time: ${apiResponseTime}ms (${(apiResponseTime / 1000).toFixed(2)}s)`);
    console.log(`⏱️ API response received at: ${new Date().toLocaleTimeString()}`);
    
    return data.message?.content || 'Sorry, I couldn\'t generate a response.';
  } catch (error) {
    console.error('Error sending message:', error);
    throw new Error('Failed to send message');
  }
};

export const extractPlaylistFromResponse = (response: string): any => {
  try {
    console.log('🔍 Original response:', response.substring(0, 200) + '...');
    
    // Remove markdown code blocks if present
    let cleanResponse = response.trim();
    
    // Handle various backtick formats
    if (cleanResponse.includes('```json')) {
      // Remove ```json ... ``` blocks
      cleanResponse = cleanResponse.replace(/```json\s*([\s\S]*?)\s*```/g, '$1');
    } else if (cleanResponse.includes('```')) {
      // Remove ``` ... ``` blocks
      cleanResponse = cleanResponse.replace(/```\s*([\s\S]*?)\s*```/g, '$1');
    }
    
    // Remove any comments that would make JSON invalid
    cleanResponse = cleanResponse.replace(/\/\/.*$/gm, ''); // Remove single-line comments
    cleanResponse = cleanResponse.replace(/\/\*[\s\S]*?\*\//g, ''); // Remove multi-line comments
    
    // Clean up any remaining whitespace
    cleanResponse = cleanResponse.trim();
    
    // Fix trailing commas that might be left after removing comments
    cleanResponse = cleanResponse.replace(/,(\s*[}\]])/g, '$1');
    
    console.log('🔍 Cleaned response:', cleanResponse.substring(0, 200) + '...');
    console.log('🔍 Starts with {:', cleanResponse.startsWith('{'));
    console.log('🔍 Ends with }:', cleanResponse.endsWith('}'));
    
    // Check if the response starts and ends with braces (complete JSON)
    if (cleanResponse.startsWith('{') && cleanResponse.endsWith('}')) {
      const jsonData = JSON.parse(cleanResponse);
      
      // Check if it's a playlist response, background-only response, or ready_to_create response
      if (jsonData.playlist || jsonData.background || jsonData.ready_to_create) {
        console.log('✅ Successfully extracted JSON with', jsonData.playlist?.songs?.length || 0, 'songs');
        return jsonData;
      }
    }
    
    // Fallback: try to find JSON in the response (for mixed responses)
    // Look for the largest JSON object in the response (most likely to be complete)
    const jsonMatches = cleanResponse.match(/\{[\s\S]*\}/g);
    console.log('🔍 Found JSON matches:', jsonMatches?.length || 0);
    if (jsonMatches) {
      // Try the last match first (most likely to be the complete response)
      for (let i = jsonMatches.length - 1; i >= 0; i--) {
        console.log('🔍 Trying JSON match', i, ':', jsonMatches[i].substring(0, 100) + '...');
        try {
          const jsonData = JSON.parse(jsonMatches[i]);
          
          // Check if it's a playlist response, background-only response, or ready_to_create response
          if (jsonData.playlist || jsonData.background || jsonData.ready_to_create) {
            console.log('✅ Successfully extracted JSON with', jsonData.playlist?.songs?.length || 0, 'songs');
            return jsonData;
          }
        } catch (parseError) {
          // Continue to next match if this one fails to parse
          continue;
        }
      }
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error extracting data from response:', error);
    return null;
  }
}; 